import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { AuthProvider, type Permission, useAuth } from '../auth/AuthProvider';
import { Forbidden, RequireAuth } from '../auth/guards';
import { ToastProvider } from '../design/Toast';
import { AuditPage } from '../features/audit/AuditPage';
import { InvitationPage } from '../features/auth/InvitationPage';
import { LoginPage } from '../features/auth/LoginPage';
import { DocumentsPage } from '../features/documents/DocumentsPage';
import { DashboardPage } from '../features/dashboard/DashboardPage';
import { OrgsPage } from '../features/orgs/OrgsPage';
import { RolesPage } from '../features/roles/RolesPage';
import { SettingsPage } from '../features/settings/SettingsPage';
import { UsersPage } from '../features/users/UsersPage';
import { LockersPage } from '../features/lockers/LockersPage';
import { LockerDetailPage } from '../features/lockers/LockerDetailPage';
import { KioskLayoutsPage } from '../features/kiosk-layouts/KioskLayoutsPage';
import { KioskLayoutEditorPage } from '../features/kiosk-layouts/KioskLayoutEditorPage';
import { MapPage } from '../features/map/MapPage';
import { OperationsPage } from '../features/operations/OperationsPage';
import { OperationDetailPage } from '../features/operations/OperationDetailPage';
import { KeysPage } from '../features/keys/KeysPage';
import { AlertsPage } from '../features/alerts/AlertsPage';
import { AutomationsPage } from '../features/automations/AutomationsPage';
import { DeliveriesPage } from '../features/notifications/DeliveriesPage';
import { IntegrationsPage } from '../features/integrations/IntegrationsPage';
import { ReportsPage } from '../features/reports/ReportsPage';
import { Shell } from '../layout/Shell';
import type { ReactNode } from 'react';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 15_000 } },
});

function Gate({ permission, children }: { permission: Permission; children: ReactNode }) {
  const { has } = useAuth();
  return has(permission) ? <>{children}</> : <Forbidden />;
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/autentificare" element={<LoginPage />} />
      <Route path="/invitatie/:token" element={<InvitationPage />} />
      <Route element={<RequireAuth />}>
        <Route element={<Shell />}>
          <Route index element={<DashboardPage />} />
          <Route
            path="/organizatii"
            element={
              <Gate permission="orgs">
                <OrgsPage />
              </Gate>
            }
          />
          <Route
            path="/utilizatori"
            element={
              <Gate permission="users">
                <UsersPage />
              </Gate>
            }
          />
          <Route
            path="/roluri"
            element={
              <Gate permission="roles">
                <RolesPage />
              </Gate>
            }
          />
          <Route
            path="/audit"
            element={
              <Gate permission="audit">
                <AuditPage />
              </Gate>
            }
          />
          <Route
            path="/setari"
            element={
              <Gate permission="settings">
                <SettingsPage />
              </Gate>
            }
          />
          <Route
            path="/documente"
            element={
              <Gate permission="documents">
                <DocumentsPage />
              </Gate>
            }
          />
          <Route
            path="/rapoarte"
            element={
              <Gate permission="reports">
                <ReportsPage />
              </Gate>
            }
          />
          <Route
            path="/automatizari"
            element={
              <Gate permission="rules">
                <AutomationsPage />
              </Gate>
            }
          />
          <Route
            path="/livrari"
            element={
              <Gate permission="integrations">
                <DeliveriesPage />
              </Gate>
            }
          />
          <Route
            path="/integrari"
            element={
              <Gate permission="integrations">
                <IntegrationsPage />
              </Gate>
            }
          />
          <Route
            path="/lockere"
            element={
              <Gate permission="fleet">
                <LockersPage />
              </Gate>
            }
          />
          <Route
            path="/lockere/:id"
            element={
              <Gate permission="fleet">
                <LockerDetailPage />
              </Gate>
            }
          />
          <Route
            path="/layouturi-kiosk"
            element={
              <Gate permission="configure">
                <KioskLayoutsPage />
              </Gate>
            }
          />
          <Route
            path="/layouturi-kiosk/:id"
            element={
              <Gate permission="configure">
                <KioskLayoutEditorPage />
              </Gate>
            }
          />
          <Route
            path="/harta"
            element={
              <Gate permission="fleet">
                <MapPage />
              </Gate>
            }
          />
          <Route
            path="/operatiuni"
            element={
              <Gate permission="operations">
                <OperationsPage />
              </Gate>
            }
          />
          <Route
            path="/operatiuni/:id"
            element={
              <Gate permission="operations">
                <OperationDetailPage />
              </Gate>
            }
          />
          <Route
            path="/chei"
            element={
              <Gate permission="keys">
                <KeysPage />
              </Gate>
            }
          />
          <Route
            path="/alerte"
            element={
              <Gate permission="alerts">
                <AlertsPage />
              </Gate>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Route>
    </Routes>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </AuthProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
}
